import { z } from 'zod';
// Stapelnaht sammel107: Status-Enum aus datenregeln.ts (eine Quelle statt drei).
import { datenregelStatusSchema } from './datenregeln.js';
import { isoDate, uuid } from './posting.js';
/**
 * `GET /api/v1/regeln/datenregeln` — die Lesesicht über die ZWEITE Regelablage,
 * `regel`/`regel_version` aus Migration `0112`. Auftrag `R-drliste`
 * (2026-09-01), Task 7 aus
 * `docs/superpowers/plans/2026-09-01-datenregeln-umsetzung.md`, `D-20`
 * (`docs/superpowers/specs/2026-09-01-datenregeln-design.md` Abschnitt 9).
 *
 * **Ein eigener Endpunkt und kein Zusatzfeld auf `regelUebersichtSchema`.**
 * Jene Antwort ist `.strict()`; ein neues Pflichtfeld darauf wäre ein BRUCH
 * (`bruch.ts` Regel 6, siehe Bericht `R-shopstorno`, Zug 106). Diese Ära
 * zeigt ein FACHLICH ANDERES Register (Datenbank statt Quelltext,
 * versioniert statt statisch) — ein eigener Antwortvertrag ist hier die
 * additive, nicht die brechende Form.
 *
 * **Dieselbe Leserechnung wie das Code-Register** (`system:read`, kein neues
 * `datenregel:*`-Recht zum Lesen — Spec Abschnitt 8: „Für das Lesen entsteht
 * keine dritte Fähigkeit"). Die beiden `datenregel:*`-Fähigkeiten selbst
 * (`manage`, `scharfschalten`) entstehen mit `R-drcaps` (Task 5) und sind
 * NICHT Teil dieses Vertrags — die Oberfläche prüft sie clientseitig gegen
 * die generische `capabilities`-Menge, die der Server ohnehin mitschickt.
 *
 * **Der Klartextsatz bleibt, und seit `R-drmaske` (2026-09-01) steht die
 * ROHFORM daneben.** `kriterienText` ist die Fassung für den Leser
 * (Textbauart wie `herkunftBeschreibung` in `kontierung.ts`) und bleibt
 * genau das. `kriterien`, `steuerschluessel`, `kundeId` und `kreditorId`
 * sind seither zusätzlich auf jeder Fassung — nicht als Zierrat, sondern
 * weil eine FORTSCHREIBUNG sie braucht: `datenregelFortschreibungSchema`
 * verlangt den vollständigen Satz, und eine Maske, die ihn nicht vorbelegen
 * kann, liesse den Sachbearbeiter beim Ändern EINES Kriteriums alle übrigen
 * Angaben neu tippen — oder sie stillschweigend verlieren. Die anfügende
 * Ablage korrigiert das nie wieder (`D-10`).
 *
 * Als `R-drliste` (Task 7) gelandet ist, stand hier das Gegenteil, und es
 * war zu seiner Zeit richtig: Der Zod-Vertrag über den geschlossenen
 * Feldkatalog (`R-drsprache`, Task 3) existierte noch nicht, und die
 * jsonb-Rohform zu spiegeln hiesse damals, auf eine Struktur zu wetten, die
 * nicht feststand. Sie steht seit Zug 107 fest.
 *
 * **`kriterien` ist trotzdem `record(unknown)` und nicht
 * `datenregelKriterienSchema`.** Diese Antwort liest den BESTAND, und der
 * Bestand ist anfügend: Eine Fassung, die vor dem Nachzug des Vertrags
 * geschrieben wurde, trägt möglicherweise eine Form, die der heutige Katalog
 * ablehnt. Ein strenges Leseschema machte die ganze Liste unlesbar, weil
 * eine einzige alte Zeile darin steht — und eine Liste, die wegen einer
 * alten Zeile gar nichts zeigt, ist schlechter als eine, die sie zeigt. Wer
 * die Rohform WEITERVERARBEITET (die Maske beim Vorbelegen), prüft sie
 * selbst gegen den Katalog und sagt es, wenn sie nicht passt.
 */
/**
 * Eine Fassung in der Historie — eine `regel_version`-Zeile, wörtlich
 * gelesen. `erstelltVon`/`scharfgeschaltetVon` sind Akteurs-Kennungen
 * (`uuid`), nicht aufgelöste Namen — dieselbe Konvention wie
 * `freigegebenVon` in `kontierung.ts`: Das Frontend löst über das bereits
 * geladene Namensverzeichnis auf (`apps/web/src/users/verzeichnis.ts`,
 * Auftrag `W-namensanzeige`), statt dass jeder Endpunkt seinen eigenen JOIN
 * trüge.
 */
export const datenregelFassungZeileSchema = z
    .object({
    id: uuid,
    status: datenregelStatusSchema,
    kriterienText: z.string(),
    /**
     * Die Kriterien, wie sie im `jsonb` stehen — die Vorlage für die
     * Fortschreibungsmaske. Siehe Kopfkommentar, warum sie hier offen und
     * nicht gegen den Katalog geprüft ist.
     */
    kriterien: z.record(z.string(), z.unknown()),
    kriterienAnzahl: z.number().int().positive(),
    konto: z.string(),
    steuerschluessel: z.string().nullable(),
    kundeId: uuid.nullable(),
    kreditorId: uuid.nullable(),
    gueltigAb: isoDate,
    gueltigBis: isoDate.nullable(),
    erstelltVon: uuid,
    erstelltAm: z.string(),
    scharfgeschaltetVon: uuid.nullable(),
    scharfgeschaltetAm: z.string().nullable(),
})
    .strict();
/**
 * Eine Zeile der Liste — eine Regel (`regel`) mit ihrer aktuellen Fassung
 * vorn und der vollständigen Fassungshistorie dahinter (`D-10`: anfügend,
 * nie geändert — die Historie IST die Tabelle, als Liste gerendert, kein
 * Diff-Betrachter, Spec Abschnitt 9).
 *
 * **`treffer30Tage` ist seit `R-drkollision` (2026-09-01) eine gemessene
 * Zahl.** Bis dahin stand hier `0` — nicht geraten, sondern als benannter
 * Rückstand: `R-drliste` hing nur an `R-drmigration` und hatte weder die
 * Kriterien-Auswertung (`R-drsprache`) noch `kontierung.regel_version_id`
 * (`R-drautomatik`, Migration `0113`) zur Verfügung, und eine zweite, dort
 * gebaute Auswertung wäre exakt die „zweite Wahrheit" gewesen, die Spec 3.4
 * ausschliesst.
 *
 * Gezählt werden jetzt die **Bewegungen**, die eine Regel in den letzten
 * dreissig Tagen automatisch gebucht hat — über
 * `kontierung.regel_version_id` und `regel_version.regel_id`, je Regel und
 * nicht je Fassung, und ausdrücklich `DISTINCT` über die Bewegung: Der
 * Automatikpfad schreibt je Buchung zwei Kontierungszeilen (Wurzel und
 * Freigabe), eine Zählung der Zeilen zeigte durchweg das Doppelte. Die
 * Ableitung steht bei der Abfrage
 * (`apps/api/src/regeln/datenregeln-liste.query.ts`).
 */
export const datenregelZeileSchema = z
    .object({
    id: uuid,
    name: z.string(),
    status: datenregelStatusSchema,
    kriterienText: z.string(),
    kriterienAnzahl: z.number().int().positive(),
    konto: z.string(),
    treffer30Tage: z.number().int().nonnegative(),
    aktuelleFassungId: uuid,
    fassungen: z.array(datenregelFassungZeileSchema),
})
    .strict();
/** Die ganze Antwort — jede Datenregel genau einmal. */
export const datenregelnListeSchema = z
    .object({
    regeln: z.array(datenregelZeileSchema),
})
    .strict();
//# sourceMappingURL=datenregeln-liste.js.map